import type { GarageStatusType } from '../types';

/**
 * Spec 062 — Garage Offers, admin review (design §3 "Admin — /api/garages/admin/offers").
 * Field names mirror the garages DTOs `AdminOfferRow`, `AdminOfferDetail`, `OfferResponse`,
 * `OfferItemView` and `BannerView`. Money arrives as JSON numbers (BigDecimal), dates as
 * `YYYY-MM-DD` (Asia/Dubai LocalDate) and timestamps as ISO instants.
 */

export type OfferStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'SCHEDULED'
  | 'LIVE'
  | 'PAUSED'
  | 'ENDED'
  | 'REJECTED'
  | 'SUSPENDED'
  | 'SUPERSEDED';

/** Statuses the admin can suspend (BR-9: SCHEDULED/LIVE/PAUSED → SUSPENDED). */
export const SUSPENDABLE_STATUSES: readonly OfferStatus[] = ['SCHEDULED', 'LIVE', 'PAUSED'];

/** The queue's status filter: the review queue plus the running statuses (suspend access). */
export const QUEUE_STATUSES: readonly OfferStatus[] = ['PENDING_APPROVAL', 'SCHEDULED', 'LIVE', 'PAUSED'];

export type OfferDiscountType = 'PERCENT' | 'FIXED';

export type GarageStatus = GarageStatusType;

export interface OfferItemView {
  offeringId: string;
  name: string;
  /** OfferingQuoteService "from" price — the before side. */
  listPrice: number;
  /** The after side. */
  discountedPrice: number;
  /** False when the service is inactive, blocked or has no bookable car. */
  bookable: boolean;
  /** Spec 073: this service's own discount value (the type is the offer's); null on a legacy row. */
  discountValue?: number | null;
  /** Spec 073: highest whole percent saving over this service's bookable variants. */
  maxPercentOff?: number;
}

/** Spec 073: one covered service and its own discount value (request side). */
export interface OfferItemRequest {
  offeringId: string;
  discountValue: number | null;
}

/** Spec 073 offer-level summary of the per-service values (OfferResponse, AdminOfferRow). */
export interface OfferDiscountSpread {
  /** The highest service value. */
  maxDiscountValue?: number | null;
  /** Highest whole percent saving across the bookable services and variants. */
  maxPercentOff?: number | null;
  /** True when the services' values differ (the label then reads "Up to …"). */
  discountVaries?: boolean;
}

export interface BannerView {
  storageKey: string;
  downloadUrl: string | null;
}

/** Subset of the garages `OfferResponse` the admin review reads. */
export interface OfferResponse extends OfferDiscountSpread {
  id: string;
  garageId: string;
  status: OfferStatus;
  /** Set on a revision row: the running offer it would replace. */
  revisionOf: string | null;
  titleEn: string;
  titleAr: string | null;
  subtitleEn: string | null;
  subtitleAr: string | null;
  startsAt: string;
  endsAt: string;
  discountType: OfferDiscountType;
  /** Spec 073: the LOWEST service value; each service's own value is on `items`. */
  discountValue: number;
  maxDiscountPerBooking: number | null;
  /** "15% OFF" / "AED 30 OFF", or "Up to 30% OFF" when the services' values differ; null while a draft has no discount. */
  label: string | null;
  maxBookings: number | null;
  bookingsUsed: number | null;
  items: OfferItemView[];
  banner: BannerView | null;
  bannerAr: BannerView | null;
  rejectionReasonCode: OfferRejectionReasonCode | null;
  rejectionReason: string | null;
  suspendReason: string | null;
  hiddenReason: 'GARAGE_BLOCKED' | 'NO_BOOKABLE_SERVICE' | null;
  submittedAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  /** The garage's open revision of this running offer (PENDING_APPROVAL or REJECTED), if any. */
  pendingRevision?: OfferResponse | null;
  /** #997: ISO instant of the last admin edit; null when never edited by an admin. */
  adminEditedAt?: string | null;
}

/** One row of `GET /api/garages/admin/offers` (AC-5). */
export interface AdminOfferRow extends OfferDiscountSpread {
  id: string;
  status: OfferStatus;
  /** Non-null when the row is an edit of a running offer (the base offer id). */
  revisionOf: string | null;
  garageId: string;
  garageName: string;
  garageStatus: GarageStatus;
  garageBlocked: boolean;
  /** The garage's SCHEDULED + LIVE + PAUSED offers. */
  garageLiveCount: number;
  /** The garage's offers and revisions waiting for approval. */
  garagePendingCount: number;
  titleEn: string;
  titleAr: string | null;
  hasArabic: boolean;
  discountType: OfferDiscountType;
  discountValue: number;
  maxDiscountPerBooking: number | null;
  label: string | null;
  items: OfferItemView[];
  startsAt: string;
  endsAt: string;
  maxBookings: number | null;
  banner: BannerView | null;
  submittedAt: string | null;
  priorRejections: number;
  version: number;
}

export interface AdminOfferGarageBlock {
  id: string;
  name: string;
  status: GarageStatus;
  blocked: boolean;
  liveCount: number;
  pendingCount: number;
}

/** `GET /api/garages/admin/offers/{id}` — the offer, its garage and the policy hints. */
export interface AdminOfferDetail {
  offer: OfferResponse;
  /** The running offer a revision would change; null for a new offer. */
  baseOffer: OfferResponse | null;
  garage: AdminOfferGarageBlock;
  /** P1 hint: a URL, phone or email pattern in any text field. */
  contactDetailsInText: boolean;
  arabicMissing: boolean;
  priorRejections: number;
}

export type OfferRejectionReasonCode =
  | 'CONTACT_DETAILS'
  | 'PRICE_MISMATCH'
  | 'UNREADABLE_IMAGE'
  | 'MISLEADING_CLAIM'
  | 'COMPETITOR_BRANDING'
  | 'ARABIC_MISSING'
  | 'OTHER';

/**
 * Picker labels, byte for byte the backend `OfferRejectionReason` labels: the server counts
 * "label + ' ' + text" towards the 10-character minimum (BR-10), so the client must use the same text.
 */
export const REJECTION_REASONS: ReadonlyArray<{ code: OfferRejectionReasonCode; label: string }> = [
  { code: 'CONTACT_DETAILS', label: 'Contact details or links in the offer' },
  { code: 'PRICE_MISMATCH', label: "Prices don't match the garage's services" },
  { code: 'UNREADABLE_IMAGE', label: 'Unreadable or low-quality image' },
  { code: 'MISLEADING_CLAIM', label: 'Misleading claim' },
  { code: 'COMPETITOR_BRANDING', label: 'Competitor branding' },
  { code: 'ARABIC_MISSING', label: 'Arabic text missing' },
  { code: 'OTHER', label: 'Other' },
];

export interface ApproveOfferRequest {
  version: number;
}

export interface RejectOfferRequest {
  reasonCode: OfferRejectionReasonCode;
  reason: string;
  version: number;
}

export interface SuspendOfferRequest {
  reason: string;
  version: number;
}

/**
 * Decision "4 preview" — `POST /api/garages/admin/offers/{id}/preview`. No body prices the offer's own
 * rule; a body (#997, admin edits) prices a draft rule over the offer's garage services.
 */
export interface OfferPreviewRequest {
  discountType: OfferDiscountType | null;
  discountValue: number | null;
  maxDiscountPerBooking: number | null;
  /** Omit for the offer's own services. */
  offeringIds?: string[];
  /** Spec 073: each service priced with its own value. */
  items?: OfferItemRequest[];
}

export type OfferPreviewSkipReason = 'NO_DISCOUNT' | 'BELOW_MIN_PRICE';

/** One price a customer could be charged, priced by the server with the checkout arithmetic. */
export interface OfferPreviewRow {
  /** Null for a flat-priced service. */
  variantId: string | null;
  /** "Toyota Camry · Sedan / Hatchback"; null for a flat price. */
  label: string | null;
  listPrice: number;
  offerPrice: number;
  saving: number;
  /** Whole percent, rounded down. */
  percentOff: number;
  discounted: boolean;
  skipReason: OfferPreviewSkipReason | null;
}

export interface OfferPreviewItem {
  offeringId: string;
  name: string;
  bookable: boolean;
  carSpecific: boolean;
  variantCount: number;
  skippedCount: number;
  maxPercentOff: number;
  /** Spec 073: the value this service was priced with. */
  discountValue?: number | null;
  /** Cheapest first. */
  variants: OfferPreviewRow[];
}

export interface OfferPreviewResponse {
  items: OfferPreviewItem[];
}

// ── #997: admins edit a garage offer ──

/**
 * `PUT /api/garages/admin/offers/{id}` — exactly the partner `OfferRequest`, with `version` required.
 * The offer keeps its status; the garage is notified with an OFFER_EDITED feed row.
 */
export interface AdminOfferUpdateRequest {
  titleEn: string;
  titleAr: string | null;
  subtitleEn: string | null;
  subtitleAr: string | null;
  /** yyyy-MM-dd (Asia/Dubai). */
  startsAt: string;
  endsAt: string;
  discountType: OfferDiscountType | null;
  /** Spec 073: sent as the lowest item value; the server recomputes MIN(items). */
  discountValue: number | null;
  /** PERCENT only. */
  maxDiscountPerBooking: number | null;
  /** 1..10000, or null for no limit. */
  maxBookings: number | null;
  /** Legacy list; ignored by the server when `items` is present. */
  offeringIds: string[];
  /** Spec 073: authoritative services and their own values. */
  items: OfferItemRequest[];
  bannerStorageKey: string | null;
  bannerArStorageKey: string | null;
  /** The `offer.version` the admin loaded (optimistic lock). */
  version: number;
}

/** `POST /api/garages/admin/offers/{id}/banner/upload-url`. */
export interface OfferBannerUploadUrlRequest {
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface OfferBannerUploadUrlResponse {
  uploadUrl: string;
  storageKey: string;
}

export type OfferHistoryAction =
  | 'OFFER_APPROVED'
  | 'OFFER_REJECTED'
  | 'OFFER_SUSPENDED'
  | 'OFFER_ENDED'
  | 'OFFER_EDITED';

/** One changed field of an OFFER_EDITED entry. Values are display strings; null = empty. */
export interface OfferFieldChange {
  field: string;
  label: string;
  before: string | null;
  after: string | null;
}

/** One row of `GET /api/garages/admin/offers/{id}/history` (newest first). */
export interface OfferHistoryEntry {
  id: number;
  occurredAt: string;
  /** `system` for automatic actions (e.g. the scheduler ending an offer). */
  adminSubjectId: string | null;
  adminName: string | null;
  action: OfferHistoryAction;
  reason: string | null;
  offerId: string;
  offerTitle: string | null;
  changes: OfferFieldChange[] | null;
}
